import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Chart } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import {
  Chart as ChartJS,
  LineController,
  LineElement,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
  ScatterController,
  ChartTypeRegistry,
} from 'chart.js';
import { format } from 'date-fns';
import { WorkItemType } from '../PerformanceDashboard';
import { BarChartOutlined, CheckOutlined, LineChartOutlined } from '@ant-design/icons';
import { Dropdown, Menu, Slider } from 'antd';
import { ChartColors, ChartDataItem, ChartType, Metric, StackingType } from '../../../interfaces/IMetricChart';
import { formatValue } from '../MetricCard';
import { MyContext } from '../../../context/myContext';
import FullDetailsModal from '../../Release Comparison/FullDetailsModal';

ChartJS.register(
  LineController,
  ScatterController,
  LineElement,
  BarElement,
  PointElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
  Legend
);


const fixedColors = Object.values(ChartColors);

const generateColor = (index: number) => fixedColors[index % fixedColors.length];


export const processData = (data: any[], weight:string | null) => {
  const processedData = data.map((dataItem) => {
    let labelsMapset: Record<string, number> = {};
    let setCountTracker: Record<string, number> = {};

    let totalValueForRelease = 0;
    let totalCountForRelease = 0;
    let totalReleasesWithValues = 0;

    if (Array.isArray(dataItem.work_item_types)) {
      dataItem.work_item_types.forEach(({ count, datasets, value, type }:WorkItemType) => {
        let totalValueForType = 0;
        const hasValidDatasets = Array.isArray(datasets) && datasets.length > 0;

        if (weight !== null) {
          if (hasValidDatasets) {
            datasets.forEach(({ set, value }) => {
              totalValueForType += value;
              labelsMapset[set] = (labelsMapset[set] || 0) + value;
              setCountTracker[set] = (setCountTracker[set] || 0) + count;
            });
          } else if (typeof value === "number") {
            totalValueForType = value;
            labelsMapset[type] = (labelsMapset[type] || 0) + value;
            setCountTracker[type] = (setCountTracker[type] || 0) + count;
          }
          totalValueForType *= count;
        } else {
          if (hasValidDatasets) {
            datasets.forEach(({ set, value }) => {
              totalValueForType += value;
              labelsMapset[set] = (labelsMapset[set] || 0) + value;
            });
          } else if (typeof value === "number") {
            totalValueForType = value;
            labelsMapset[type] = (labelsMapset[type] || 0) + value;
          }
        }

        totalValueForRelease += totalValueForType;
        totalCountForRelease += count;
      });
    }
    if (totalValueForRelease > 0) {
      totalReleasesWithValues++;
    }
    let finalValue = 0;

    if (weight !== null) {
      finalValue = totalCountForRelease
        ? totalValueForRelease / totalCountForRelease
        : 0;

      Object.keys(labelsMapset).forEach((set) => {
        const divisor = setCountTracker[set];
        labelsMapset[set] = divisor ? labelsMapset[set] / divisor : 0;
      });
    } else {
      finalValue =  totalValueForRelease / totalReleasesWithValues;
    }

    return {
      date: dataItem.date,
      value: parseFloat(finalValue.toFixed(1)),
      release: dataItem.release || "",
      labelsMapset,
    };
  });

  return processedData;
};

export const calculateRollingAverage = (data: any[], windowSize: number, weight: string) => {
  if (!data || data.length < 1) {
    return data;
  }

  const result: any[] = [];

  for (let i = 0; i < data.length; i++) {
    const windowItems = data.slice(Math.max(0, i - windowSize + 1), i + 1);

    let totalWeightedSum = 0;
    let totalCountForWindow = 0;
    let totalReleasesWithValues = 0;

    windowItems.forEach(release => {

      if (release.work_item_types?.length) {
        release.work_item_types.forEach(({ count, datasets, value, type }: any) => {
          let totalValueForType = 0;
          const hasValidDatasets = Array.isArray(datasets) && datasets.length > 0;

          if (weight !== null) {
            if (hasValidDatasets) {
              datasets.forEach(({ value }: any) => {
                totalValueForType += value;
              });
            } else if (typeof value === "number") {
              totalValueForType = value;
            }
            totalValueForType *= count;
          } else {
            if (hasValidDatasets) {
              datasets.forEach(({ value }: any) => {
                totalValueForType += value;
              });
            } else if (typeof value === "number") {
              totalValueForType = value;
            }
          }

          totalWeightedSum += totalValueForType;
          totalCountForWindow += count;

        });
      }

      if (totalWeightedSum > 0) {
        totalReleasesWithValues++;
      }
    });

    let rollingAvgValue;

    if (weight !== null) {
      rollingAvgValue = totalCountForWindow > 0
        ? totalWeightedSum / totalCountForWindow
        : 0;
    } else {
      rollingAvgValue = totalWeightedSum > 0
        ? totalWeightedSum / totalReleasesWithValues
        : 0;
    }

    const lastReleaseInWindow = windowItems[windowItems.length - 1];

    result.push({
      date: lastReleaseInWindow.date,
      value: parseFloat(rollingAvgValue.toFixed(1)),
      release: lastReleaseInWindow.release || "",
    });
  }

  return result;
};


interface ReleaseMetricsChartProps {
  selectedMetricY1?: Metric;
  selectedMetricY2?: Metric;
  updateFilter: (key: string, value: any) => void
  selectedTypeY1: string | undefined;
  setSelectedTypeY1: React.Dispatch<React.SetStateAction<string>>;
  selectedTypeY2: string | undefined;
  setSelectedTypeY2: React.Dispatch<React.SetStateAction<string>>;
  selectedStackingY1: string | undefined;
  setSelectedStackingY1: React.Dispatch<React.SetStateAction<string>>;
  selectedStackingY2: string | undefined;
  setSelectedStackingY2: React.Dispatch<React.SetStateAction<string>>;
  setRollingWindowSizeY1:React.Dispatch<React.SetStateAction<number>>;
  rollingWindowSizeY1:number;
  setRollingWindowSizeY2:React.Dispatch<React.SetStateAction<number>>;
  rollingWindowSizeY2:number;
  rollingAverageMaxCountForY1:number;
  rollingAverageMaxCountForY2:number;
}

const MetricsChart: React.FC<ReleaseMetricsChartProps> = ({
  selectedMetricY1,
  selectedMetricY2,
  updateFilter,
  selectedTypeY1,
  setSelectedTypeY1,
  selectedTypeY2,
  setSelectedTypeY2,
  selectedStackingY1,
  setSelectedStackingY1,
  selectedStackingY2,
  setSelectedStackingY2,
  setRollingWindowSizeY1,
  rollingWindowSizeY1,
  setRollingWindowSizeY2,
  rollingWindowSizeY2,
  rollingAverageMaxCountForY1,
  rollingAverageMaxCountForY2
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedReleaseItem, setSelectedReleaseItem] = useState<any>(null);
  const [chartDataY1, setChartDataY1] = useState<ChartDataItem[]>([]);
  const [chartDataY2, setChartDataY2] = useState<ChartDataItem[]>([]);
  const [chartInstance, setChartInstance] = useState<any>(null);
  const [menuClicked, setMenuClicked] = useState(false);
  const {  releaseDetails } = useContext<any>(MyContext);

  useEffect(() => {
    if (selectedMetricY1) {
      setChartDataY1(processData(selectedMetricY1.data, selectedMetricY1.weight));
      if (selectedMetricY2) {
        setChartDataY2(processData(selectedMetricY2.data, selectedMetricY2.weight));
      } else {
        setChartDataY2([]);
      }
    }
  }, [selectedMetricY1, selectedMetricY2]);

  const chartData = useMemo(() => {
    const datasets: any[] = [];

    if (selectedMetricY1 && chartDataY1.length > 0) {
      const mainData = chartDataY1.map((item) => ({
        x: new Date(item.date).getTime(),
        y: item.value ??0,
        release_version: item.release,
      }));

      const rolling_avg_data = calculateRollingAverage(selectedMetricY1.data, rollingWindowSizeY1 || 5, selectedMetricY1.weight)
      const rolling_avg_chartData = rolling_avg_data.map((item) => ({
        x: new Date(item.date).getTime(),
        y: item.value ??0,
        release_version: item.release,
      }));

      if ((selectedStackingY1 === StackingType.STACKED || selectedStackingY1===StackingType.STACKED_100) && Object.keys(chartDataY1[0].labelsMapset || {}).length > 0) {
        
        const uniqueLabels = Array.from(
          new Set(chartDataY1.flatMap(item => Object.keys(item.labelsMapset || {})))
        );
        
        const normalizedData = chartDataY1.map((item) => {
          const totalValuePerBar = uniqueLabels.reduce((sum, label) => sum + (item.labelsMapset?.[label] ?? 0), 0) || 1;
          return {
            ...item,
            normalizedValues: uniqueLabels.reduce((acc, label) => {
              const value = item.labelsMapset?.[label] ?? 0;
              acc[label] = (value / totalValuePerBar) * 100;
              return acc;
            }, {} as Record<string, number>),
          };
        });
        
        uniqueLabels.forEach((label,index) => {
          datasets.push({
            type: "bar" as keyof ChartTypeRegistry,
            label: `${selectedMetricY1.name} (${label})`,
            data: normalizedData.map((item) => ({
              x: new Date(item.date).getTime(),
              y: selectedStackingY1 === StackingType.STACKED_100 ? item.normalizedValues[label]??0: item.labelsMapset?.[label] ?? 0,
              release_version: item.release,
            })),
            backgroundColor:  generateColor(index),
            yAxisID: "y",
            stack: "stack1",
            barThickness: 14
          });
        });
      } else {
        const baseDataset = {
          type: selectedTypeY1 as keyof ChartTypeRegistry,
          label: `${selectedMetricY1.name}`,
          data: selectedTypeY1==='line' ? rolling_avg_chartData : mainData,
          borderColor: "rgba(91, 192, 235, 1)",
          backgroundColor: "rgba(91, 192, 235, 0.3)",
          yAxisID: "y",
          tension: rollingWindowSizeY1 >= 2 ?  0.8 : 0.1,
          ...(selectedTypeY1 === "bar" && { barThickness: 14 }),
        };
        
        datasets.push(baseDataset);
      }
    }

    if (selectedMetricY2 && chartDataY2.length > 0) {

      const rolling_avg_data = calculateRollingAverage(selectedMetricY2.data, rollingWindowSizeY2 || 5, selectedMetricY2.weight)
      const rolling_avg_chartData = rolling_avg_data.map((item) => ({
        x: new Date(item.date).getTime(),
        y: item.value ??0,
        release_version: item.release,
      }));

      const mainData = chartDataY2.map((item) => ({
        x: new Date(item.date).getTime(),
        y: item.value ??0,
        release_version: item.release,
      }));
      
      if ((selectedStackingY2 ===  StackingType.STACKED ||selectedStackingY2===StackingType.STACKED_100) && Object.keys(chartDataY2[0].labelsMapset || {}).length > 0) {
        const uniqueLabels = Array.from(
          new Set(chartDataY2.flatMap(item => Object.keys(item.labelsMapset || {})))
        );
        
        const normalizedData = chartDataY2.map((item) => {
          const totalValuePerBar = uniqueLabels.reduce((sum, label) => sum + (item.labelsMapset?.[label] ?? 0), 0) || 1;
          return {
            ...item,
            normalizedValues: uniqueLabels.reduce((acc, label) => {
              const value = item.labelsMapset?.[label] ?? 0;
              acc[label] = (value / totalValuePerBar) * 100;
              return acc;
            }, {} as Record<string, number>),
          };
        });
        
        
        uniqueLabels.forEach((label,index) => {
          datasets.push({
            type: "bar" as keyof ChartTypeRegistry,
            label: `${selectedMetricY2.name} (${label})`,
            data: normalizedData.map((item) => ({
              x: new Date(item.date).getTime(),
              y: selectedStackingY2 === StackingType.STACKED_100 ? item.normalizedValues[label]??0: item.labelsMapset?.[label] ?? 0,
              release_version: item.release,
            })),
            backgroundColor:  generateColor(index),
            yAxisID: "y1",
            stack: "stack2",
            barThickness: 14
          });
        });
      }else {
        const baseDataset = {
          type: selectedTypeY2 as keyof ChartTypeRegistry,
          label: `${selectedMetricY2.name}`,
          data: selectedTypeY2==='line' ? rolling_avg_chartData : mainData,
          backgroundColor: selectedTypeY2===ChartType.SCATTER? "#FFCE56":"rgba(160, 160, 160, 0.5)",
          borderColor: "rgba(160, 160, 160, 1)",
          yAxisID: "y1",
          tension: rollingWindowSizeY2 >= 2 ? 0.8 : 0.1,
          ...(selectedTypeY2 === "bar" && { barThickness: 14 }),
        };
        
        datasets.push(baseDataset);
      }
    }

    if (datasets.length === 0) {
      datasets.push({
        type: "bar" as keyof ChartTypeRegistry,
        label: "No data",
        data: [],
        yAxisID: "y"
      });
    }

    return { datasets };
  }, [
    chartDataY1, 
    chartDataY2, 
    selectedMetricY1, 
    selectedMetricY2, 
    selectedTypeY1, 
    selectedTypeY2, 
    selectedStackingY1, 
    selectedStackingY2,
    rollingWindowSizeY1,
    rollingWindowSizeY2
  ]);

  const options = useMemo(() => ({
    maintainAspectRatio: false,
    animation: { duration: 0 },
    responsive: true,
    interaction: {
      mode: 'point' as const,
      intersect: true,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          title: function (tooltipItems: TooltipItem<'line' | 'bar'>[]) {
            if (!tooltipItems.length) return 'N/A';
            
            const item = tooltipItems[0];
            const dataIndex = item.dataIndex;
            const datasetIndex = item.datasetIndex;
            
            let releaseVersion = 'N/A';
            if (datasetIndex === 0 && chartDataY1[dataIndex]) {
              releaseVersion = chartDataY1[dataIndex].release;
            } else if (selectedMetricY2 && chartDataY2[dataIndex]) {
              releaseVersion = chartDataY2[dataIndex].release;
            }
            
            const xValue = (item.raw as { x?: number })?.x;
            const formattedDate = xValue
              ? format(new Date(xValue), 'dd MMM yyyy')
              : 'N/A';
            
              return `Date: ${formattedDate}\nRelease Version: ${releaseVersion}`;

          },
          label: function (tooltipItem: TooltipItem<'line' | 'bar'>) {
            const { dataset, parsed } = tooltipItem;
            const yValue = parsed.y;
            if (yValue == null || isNaN(yValue)) {
              return `${dataset.label}: N/A`;
            }
  
            if (dataset?.label && dataset?.label?.includes('Trendline')) {
              return "";
            }
            const roundedValue = formatValue(yValue);

            if ((selectedStackingY1 === StackingType.STACKED_100 && dataset.yAxisID === 'y') ||
                (selectedStackingY2 === StackingType.STACKED_100 && dataset.yAxisID === 'y1')) {
              return `${dataset.label}: ${roundedValue}%`;
            }
  
            if (dataset.yAxisID === 'y') {
              const units = selectedMetricY1?.units ? ` ${selectedMetricY1.units}` : '';
              return `${dataset.label}: ${roundedValue}${units}`;
            } else if (dataset.yAxisID === 'y1') {
              const units = selectedMetricY2?.units ? ` ${selectedMetricY2.units}` : '';
              return `${dataset.label}: ${roundedValue}${units}`;
            }
            return '';
          }
        }
      },
    },
    onClick: (
      event: any,
      elements: string | any[],
      chart: { data: { datasets: { [x: string]: any }[] } }
    ) => {
      if (elements.length === 0) return;
    
      const { datasetIndex, index } = elements[0];
      const dataset = chart.data.datasets[datasetIndex];
      const point = dataset.data[index] as {
        x: number;
        y: number;
        release_version?: string;
      };
    
      const releaseVersion = point.release_version || 'N/A';
    
    const filteredRelease = releaseDetails
    .flatMap((detail: { releases: any; }) => detail.releases ?? [])
    .find((release: { release: string; }) => release.release === releaseVersion);

    setSelectedReleaseItem(filteredRelease || null);
    setIsModalVisible(true);
    },
    scales: {
      x: {
        type: 'time' as const,
        time: {
          unit: 'month' as const,
        },
        title: {
          display: true,
        },
        ticks: {
          align: 'start' as const,
          source: 'auto' as const,
          callback: function (value: string | number | Date) {
            return format(new Date(value), 'MMM yyyy');
          },
        },
        grid: {
          offset: false,
        },
      },
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        stacked: selectedStackingY1 === StackingType.STACKED || selectedStackingY1 === StackingType.STACKED_100,
        max: selectedStackingY1 === StackingType.STACKED_100 ? 100 : undefined,
        title: {
          display: true,
          text: `${selectedMetricY1?.name}${selectedMetricY1?.units ? ` (${selectedMetricY1?.units})` : ''}`,
        },
        min: 0,
      },
      y1: {
        type: 'linear' as const,
        display: !!selectedMetricY2,
        position: 'right' as const,
        stacked: selectedStackingY2 ===  StackingType.STACKED  ||  selectedStackingY2 === StackingType.STACKED_100,
        max: selectedStackingY1 === StackingType.STACKED_100 ? 100 : undefined,
        title: {
          display: true,
          text: `${selectedMetricY2?.name}${selectedMetricY2?.units ? ` (${selectedMetricY2?.units})` : ''}`,
        },
        grid: {
          drawOnChartArea: false,
        },
        min: 0,
      },
    },
  }), [
    selectedMetricY1, 
    selectedMetricY2, 
    selectedStackingY1, 
    selectedStackingY2, 
    chartDataY1, 
    chartDataY2,
  ]);

  useEffect(() => {
    if (menuClicked && (selectedTypeY1 === ChartType.LINE || selectedTypeY2 === ChartType.SCATTER)) {
      setSelectedStackingY1(StackingType.SIMPLE);
      updateFilter('selectedStackingY1', StackingType.SIMPLE);
      setMenuClicked(false);
    }
    
  }, [selectedTypeY1]);

  useEffect(() => {
    if (menuClicked && (selectedTypeY2 === ChartType.LINE || selectedTypeY2 === ChartType.SCATTER)) {
      setSelectedStackingY2(StackingType.SIMPLE);
      updateFilter('selectedStackingY2', StackingType.SIMPLE);
      setMenuClicked(false);
    }
  }, [selectedTypeY2]);

  const handleMenu1Click = (key: string) => {
    setMenuClicked(true);
    if (Object.values(ChartType).includes(key as ChartType)) {
      updateFilter('selectedTypeY1', key as ChartType);
      setSelectedTypeY1(key as ChartType);

    } else if (Object.values(StackingType).includes(key as StackingType)) {
      setSelectedStackingY1(key as StackingType);
      updateFilter('selectedStackingY1', key as StackingType);
    }
  
    if (chartInstance) {
      chartInstance.update();
    }
  };
  
  const handleMenu2Click = (key: string) => {
    setMenuClicked(true);
    if (Object.values(ChartType).includes(key as ChartType)) {
      updateFilter('selectedTypeY2', key as ChartType);
      setSelectedTypeY2(key as ChartType);
    } else if (Object.values(StackingType).includes(key as StackingType)) {
      setSelectedStackingY2(key as StackingType);
      updateFilter('selectedStackingY2', key as StackingType);
    } 
    if (chartInstance) {
      chartInstance.update();
    }
  };
  
  const handleCancel = () => {
    setIsModalVisible(false);
    setSelectedReleaseItem(null);
};

  const dropdownMenu1 = (
    <Menu onClick={({ key }) => handleMenu1Click(key)}>
      <Menu.ItemGroup>
        {Object.values(ChartType).map((type) => (
          <Menu.Item key={type}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ width: "16px", marginRight: "8px" }}>
                {selectedTypeY1 === type && <CheckOutlined />}
              </div>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </div>
          </Menu.Item>
        ))}
      </Menu.ItemGroup>

      {selectedTypeY1 === ChartType.BAR && (
        <>
          <Menu.Divider />
          <Menu.ItemGroup>
            {Object.values(StackingType).map((option) => (
              <Menu.Item
                key={option}
                disabled={
                  selectedStackingY2 === StackingType.STACKED ||
                  selectedStackingY2 === StackingType.STACKED_100
                }
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ width: "16px", marginRight: "8px" }}>
                    {selectedStackingY1 === option && <CheckOutlined />}
                  </div>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </div>
              </Menu.Item>
            ))}
          </Menu.ItemGroup>
        </>
      )}
      {selectedTypeY1==='line' && rollingAverageMaxCountForY1 > 5 && 
        <>
            <Menu.Divider />

            <Menu.Item key="rolling_curve" style={{ cursor: "default" }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "start" }}>
                  <h1>Rolling Average</h1>
                  <Slider
                    min={1}
                    max={rollingAverageMaxCountForY1}
                    value={rollingWindowSizeY1}
                    onChange={(value) => {
                      setRollingWindowSizeY1(value);
                      updateFilter('rollingWindowSizeY1', value);
                    }}
                    tooltip={{ placement: 'top' }}
                    tipFormatter={(value) => `${value ?? 0} Release${value === 1 ? '' : 's'}`}
                    style={{ width: 160 }}
                  />
                </div>
            </Menu.Item>
        </>
      }
    </Menu>
  );

  const dropdownMenu2 = (
    <Menu onClick={({ key }) => handleMenu2Click(key)}>
      <Menu.ItemGroup>
        {Object.values(ChartType).map((type) => (
          <Menu.Item key={type}>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ width: "16px", marginRight: "8px" }}>
                {selectedTypeY2 === type && <CheckOutlined />}
              </div>
              {type.charAt(0).toUpperCase() + type.slice(1)}
            </div>
          </Menu.Item>
        ))}
      </Menu.ItemGroup>

      {selectedTypeY2 === ChartType.BAR && (
        <>
          <Menu.Divider />
          <Menu.ItemGroup>
            {Object.values(StackingType).map((option) => (
              <Menu.Item
                key={option}
                disabled={
                  selectedStackingY1 === StackingType.STACKED ||
                  selectedStackingY1 === StackingType.STACKED_100
                }
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ width: "16px", marginRight: "8px" }}>
                    {selectedStackingY2 === option && <CheckOutlined />}
                  </div>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </div>
              </Menu.Item>
            ))}
          </Menu.ItemGroup>
        </>
      )}
      {selectedTypeY2==='line' && rollingAverageMaxCountForY2 > 5 && 
      <>
        <Menu.Divider />
        <Menu.Item key="rolling_curve" style={{ cursor: "default" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "start" }}>
          <h1>Rolling Average</h1>
            <Slider
              min={1}
              max={rollingAverageMaxCountForY2}
              value={rollingWindowSizeY2}
              onChange={(value) => {
                setRollingWindowSizeY2(value);
                updateFilter('rollingWindowSizeY2', value);
              }}
              tooltip={{ placement: 'top' }}
              tipFormatter={(value) => `${value ?? 0} Release${value === 1 ? '' : 's'}`}
              style={{ width: 160 }}
            />
          </div>
        </Menu.Item>
      </>
      }
    </Menu>
  );
  
  return (
    <>
  <Chart type="bar" data={chartData} options={options}  ref={(ref) => {if (ref) {setChartInstance(ref);}}}/>
      <Dropdown overlay={dropdownMenu1} trigger={["click"]}>
        <LineChartOutlined
          style={{
            position: "absolute",
            left: "15px",
            top: "60px",
            fontSize: "18px",
            cursor: "pointer",
            color:'rgba(91, 192, 235, 1)',
          }}
        />
      </Dropdown>

      <Dropdown overlay={dropdownMenu2} trigger={["click"]}>
        <BarChartOutlined
          style={{
            position: "absolute",
            right: "15px",
            top: "60px",
            fontSize: "18px",
            cursor: "pointer",
            color:'gray'
          }}
        />
      </Dropdown>


      {isModalVisible && (
                <FullDetailsModal
                    onClose={handleCancel}
                    releaseItem={selectedReleaseItem}
                    selectedOptions={{ firstPass: true, rework: true }}
                />
            )}

    </>
  );
};

export default MetricsChart;